import { useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { IDropdown, IBaseReq } from '../../../models/BaseModel';
import { useEffect, useState } from 'react';
import { Fetch } from '../../../shared/Fetch';
import { JVDropdown } from '../../../shared/JVDropdown';
import { JVDate } from '../../../shared/JVDate';
import { JVInput } from '../../../shared/JVInput';
import { JVTextArea } from '../../../shared/JVTextarea';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';

interface IPackageDrop extends IDropdown {
    duration: number
}
const schema = yup.object({
    purposeOfTripId: yup.number().required("Purpose of Trip is required").min(1, "Purpose of Trip is required"),
    hotelId: yup.number().required("Hotel is required").min(1, "Hotel is required"),
    packageId: yup.number().required("Package is required").min(1, "Package is required"),
    checkin: yup.date().required("Check In Date is required").typeError("Check In Date is required"),
    checkout: yup.date().required("Check Out Date is required").typeError("Check Out Date is required"),
    noOfAdults: yup.number().typeError('No Of Adults is required').required("No Of Adults is required").min(1, "No Of Adults is required"),
    noOfChildren: yup.number().typeError("No Of Children is required").required("No Of Children is required").min(0, "No Of Children is required"),
    mealPlan: yup.string().typeError('Meal Plan is Required').required("Meal Plan is Required"),
    comment: yup.string().nullable(),
});

export const BaseReq = ({ req, toast, onSuccess, open, onHide }: { req: IBaseReq, toast: any, onSuccess: () => void, open: boolean, onHide: () => void }) => {
    const [submitting, setSubmitting] = useState(false);
    const [purposeOfTrip, setPurposeOfTrip] = useState<IDropdown[]>();
    const [packages, setPackages] = useState<IPackageDrop[]>();
    const [hotels, setHotels] = useState<IDropdown[]>();
    const [countries, setCountries] = useState<IDropdown[]>();
    const [states, setStates] = useState<IDropdown[]>();
    const [cities, setCities] = useState<IDropdown[]>();

    const { control, handleSubmit, watch, formState: { errors }, setValue, reset } = useForm<IBaseReq>({
        resolver: yupResolver(schema),
        defaultValues: {},
        shouldFocusError: false,
    });

    useEffect(() => { if (open) reset(req) }, [req]);

    const countryId = watch('countryId');
    const stateId = watch('stateId');
    const checkin = watch("checkin");
    const cityId = watch("cityId");
    const packageId = watch("packageId");

    useEffect(() => {
        if (open && checkin && packageId && !req.id) {
            const checkInDate = new Date(checkin);
            const packageData = packages?.find(p => p.id === packageId);
            if (packageData) {
                const duration = packageData.duration || 0;
                const checkOutDate = new Date(checkInDate.getTime() + duration * 60 * 60 * 1000);
                setValue('checkout', checkOutDate);
            }
        }
    }, [checkin, packageId]);

    const loadPurposeOfTrip = async () => {
        const response = await Fetch("purposeoftrip/dropdown", "GET", null, null, null);
        if (response) {
            setPurposeOfTrip(await response.json());
        }
    }
    const loadPackages = async (cityId: number) => {
        const response = await Fetch(`package/dropdown/${cityId}`, "GET", null, null, null);
        if (response) {
            setPackages(await response.json());
        }
    }
    const loadHotels = async (cityId: number) => {
        const response = await Fetch(`hotel/dropdown/${cityId}`, "GET", null, null, null);
        if (response) {
            setHotels(await response.json());
        }
    }
    const loadCities = async (stateId: number) => {
        const response = await Fetch(`city/touristplaces/${stateId}`, "GET", null, null, null);
        if (response) {
            setCities(await response.json());
        }
    }
    const loadStates = async (countryId: number) => {
        const response = await Fetch(`state/touristplaces/${countryId}`, "GET", null, null, null);
        if (response) {
            setStates(await response.json());
        }
    }
    const loadCountries = async () => {
        const response = await Fetch(`country/touristplaces`, "GET", null, null, null);
        if (response) {
            setCountries(await response.json());
        }
    }

    useEffect(() => {
        loadCountries();
        loadPurposeOfTrip();
    }, []);
    useEffect(() => { if (countryId) loadStates(countryId) }, [countryId]);
    useEffect(() => { if (stateId) loadCities(stateId) }, [stateId]);

    useEffect(() => {
        if (cityId) {
            loadPackages(cityId);
            loadHotels(cityId);
        }
    }, [cityId]);

    const saveToDB = async (body: IBaseReq) => {
        setSubmitting(true);
        const method = req.id ? "PUT" : "POST";
        const message = req.id ? "Updated Successfully" : "Added Successfully";

        body.leadId = req.leadId;

        const response = await Fetch(`leadreq${req.id ? '/' + req.id : ''}`, method, body, null, toast);
        if (response) {
            toast.current.show({ severity: 'success', detail: message, life: 3000 });
            onSuccess();
            reset();
        }
        setSubmitting(false);
    }

    function toLocalISOString(date: Date) {
        const pad = (n: number) => n.toString().padStart(2, "0");
        return (
            date.getFullYear() +
            "-" + pad(date.getMonth() + 1) +
            "-" + pad(date.getDate()) +
            "T" + pad(date.getHours()) +
            ":" + pad(date.getMinutes()) +
            ":" + pad(date.getSeconds())
        );
    }
    function onSubmit(data: IBaseReq) {
        if (data.checkin) data.checkin = toLocalISOString(new Date(data.checkin)) as any;
        if (data.checkout) data.checkout = toLocalISOString(new Date(data.checkout)) as any;
        saveToDB(data);
    }

    return (
        <Dialog visible={open} style={{ width: '80vw' }} header={`Add/Edit Package & Checkin`} modal className="p-fluid" onHide={onHide}>
            <form onSubmit={handleSubmit(onSubmit)} className="p-fluid formgrid grid">
                <JVDropdown column={4} required label="Purpose of Trip" control={control} options={purposeOfTrip} optionLabel="name" optionValue="id" name="purposeOfTripId" placeholder="Select Purpose of Trip" error={errors.purposeOfTripId?.message} />
                <JVDropdown column={4} required control={control} label="Country" options={countries} optionLabel="name" optionValue="id" name="countryId" error={errors.countryId?.message} />
                <JVDropdown column={4} required control={control} label="State" options={states} optionLabel="name" optionValue="id" name="stateId" error={errors.stateId?.message} />
                <JVDropdown column={4} required control={control} label="City" options={cities} optionLabel="name" optionValue="id" name="cityId" error={errors.cityId?.message} />
                <JVDropdown column={4} required label="Package" control={control} options={packages} optionLabel="name" optionValue="id" name="packageId" placeholder="Select Package" error={errors.packageId?.message} />
                <JVDropdown column={4} required label="Hotel" control={control} options={hotels} optionLabel="name" optionValue="id" name="hotelId" placeholder="Select Hotel" error={errors.hotelId?.message} />
                <JVDate showTime column={4} minDate={new Date()} required label="Check In" control={control} name="checkin" error={errors.checkin?.message} />
                <JVDate column={4} minDate={new Date(checkin || new Date())} required label="Check Out" control={control} name="checkout" error={errors.checkout?.message} />
                <JVDropdown required column={4} label="Meal Plan" optionLabel="" optionValue="" options={["EP", "AP", "CP", "MAP"]} placeholder="Meal Plan" control={control} name="mealPlan" error={errors.mealPlan?.message} />
                <JVInput column={4} type="number" required label="Adults" control={control} name="noOfAdults" error={errors.noOfAdults?.message} />
                <JVInput column={4} type="number" required label="Children (7 to 12 years)" control={control} name="noOfChildren" error={errors.noOfChildren?.message} />
                <JVTextArea column={1} label="Special Request" control={control} name="comment" placeholder="Write your special request here..." />
                <div className="col-12 md:col-12 flex gap-4 justify-content-end">
                    <Button style={{ width: '100px' }} loading={submitting} label="SAVE" type="submit" className="p-button-rounded"></Button>
                    <Button style={{ width: '100px' }} label="CLOSE" onClick={onHide} type="button" className="p-button-rounded p-button-outlined surface-200"></Button>
                </div>
            </form>
        </Dialog>
    )
}